import { useEffect, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import type { ProductHit } from '@/lib/api-types'
import { formatMoney } from '@/lib/format'
import { STOCK_BADGE_CLASSES, STOCK_BADGE_LABELS } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Field, FieldHint, MicroLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'

/**
 * Debounced catalog search over GET /v1/client/products/search — the block
 * the purchasing wizard established, extracted so the Requests forms pick
 * products from the same UI. Results render name/item code, the stock badge,
 * and (unless `showPrices` is off) the cheapest per-kg price; the caller owns
 * what "adding" means (the wizard appends a line, a request captures
 * {id, name}).
 */
export function ProductSearch({
  id,
  label,
  addedIds,
  atCap = false,
  capMessage,
  unavailableMessage = 'Pricing isn’t set up for your account yet — contact your Green Line rep.',
  showPrices = true,
  onAdd,
}: {
  /** Input id — also prefixes the hint id (`${id}-hint`). */
  id: string
  /** Visible micro-label over the search input. */
  label: string
  /** Products already picked — their rows read "Added" and disable. */
  addedIds: Set<string>
  /** True when the caller can't take more products (disables every Add). */
  atCap?: boolean
  /** Note rendered under the input while `atCap` (why adding is off). */
  capMessage?: string
  /**
   * Copy for the 409 no_pricebook state. The default speaks the wizard's
   * pricing language; non-pricing contexts (the Requests forms) pass
   * availability copy instead.
   */
  unavailableMessage?: string
  /** Show each hit's "from $/kg" — the wizard passes `flags.purchasingPricing`. */
  showPrices?: boolean
  onAdd: (product: ProductHit) => void
}) {
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleTerm(value: string) {
    setTerm(value)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setDebounced(value.trim()), 250)
  }
  // Unmount-only cleanup — clearing on every render would cancel the debounce.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const enabled = debounced.length >= 2
  const query = useQuery({
    queryKey: ['product-search', debounced],
    queryFn: ({ signal }) => api.searchProducts(debounced, { signal }),
    enabled,
    staleTime: 60_000,
  })

  const noPricebook = query.error instanceof ApiError && query.error.code === 'no_pricebook'

  return (
    <div>
      <Field>
        <MicroLabel htmlFor={id}>{label}</MicroLabel>
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-mute"
          />
          <Input
            id={id}
            type="search"
            value={term}
            onChange={(event) => handleTerm(event.target.value)}
            placeholder="Search by product name or item code"
            className="pl-9"
            aria-describedby={`${id}-hint`}
          />
        </div>
        <FieldHint id={`${id}-hint`}>Type at least two characters to search your catalog.</FieldHint>
        {atCap && capMessage ? (
          <p className="text-xs font-medium text-inkMid">{capMessage}</p>
        ) : null}
      </Field>

      {enabled ? (
        <div className="mt-3" aria-live="polite">
          {query.isPending ? (
            <Spinner label="Searching…" />
          ) : noPricebook ? (
            <p className="text-sm text-rust">{unavailableMessage}</p>
          ) : query.isError ? (
            <p className="text-sm text-rust">The search didn&rsquo;t load. Adjust the term and try again.</p>
          ) : query.data.products.length === 0 ? (
            <p className="text-sm text-mute">No catalog match for “{debounced}”.</p>
          ) : (
            <ul className="space-y-2">
              {query.data.products.map((product) => {
                const added = addedIds.has(product.id)
                const disabled = added || atCap
                // Cheapest per-kg price across the product's pack options.
                const from = showPrices && product.tiers.length
                  ? Math.min(...product.tiers.map((tier) => tier.pricePerKg))
                  : null
                return (
                  <li key={product.id}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onAdd(product)}
                      className={cn(
                        'flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1.5 rounded-lg border border-rule/70 bg-card px-4 py-3 text-left transition-all duration-[180ms] ease-editorial',
                        disabled
                          ? 'opacity-60'
                          : 'hover:border-brand/50 hover:bg-cream',
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-ink">
                          {product.name}
                        </span>
                        <span className="mt-0.5 block font-mono text-xs text-mute">
                          {product.itemCode ?? '—'}
                          {product.family ? ` · ${product.family}` : ''}
                        </span>
                      </span>
                      <span className="flex items-center gap-2.5">
                        <StockBadge product={product} />
                        {from !== null ? (
                          <span className="whitespace-nowrap font-mono text-xs text-inkMid">
                            from {formatMoney(from)}/kg
                          </span>
                        ) : null}
                        <span className="text-sm font-semibold text-brand">
                          {added ? 'Added' : 'Add'}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}

/** Stock badge + made-to-order lead-time hint (shared with the wizard's line cards). */
export function StockBadge({ product }: { product: ProductHit }) {
  return (
    <span className="flex items-center gap-1.5">
      <StatusChip
        label={STOCK_BADGE_LABELS[product.stockBadge]}
        chipClasses={STOCK_BADGE_CLASSES[product.stockBadge]}
      />
      {product.stockBadge === 'made_to_order' && product.leadTimeWeeks ? (
        <span className="whitespace-nowrap text-xs text-mute">
          ~{product.leadTimeWeeks} wk lead
        </span>
      ) : null}
    </span>
  )
}
